import { PendingButton } from '../components/common/PendingButton';
import type { PortalStay, PortalDog, PortalMe } from '@bbbk/schema';
import { toIrishNational } from '@bbbk/schema';
import { Avatar } from '../components/common';
import { WizardBrand } from './RegistrationChrome';
import { G } from './glyphs';
import { Tag } from './Tag';
import { initialOf } from './RegistrationDetails';
import {
  cardTag,
  closingLine,
  dogLine,
  dogsOnStay,
  halfOfDay,
  nextStay,
  nights,
  receiptLead,
  stayDay,
  stayHead,
  townCounty,
} from './registrationRules';

// `c2j` / `c2g` / `c2i` / `c2h` — All set (AC30–AC35).
//
// The receipt of everything submitted: the hero, then the household — every
// person, every dog, the town — then either the invitation to book (nothing in
// the book yet) or the stay Patricia already took at the desk (decision 64).

function CompactStay({ stay, dogs }: { stay: PortalStay; dogs: PortalDog[] }) {
  const on = dogsOnStay(stay, dogs);
  // `dogsOnStay` returns NOTHING when the stay names no dog the household still
  // holds — an archived dog is the live case (Codex review, PR #228). It used
  // to hand back every dog instead, which drew the wrong animals onto a real
  // stay. The dates are still true and still worth showing, so the card keeps
  // them and says plainly whose stay it is, using the names the booking itself
  // carries.
  const head = on.length > 0
    ? stayHead(on)
    : { names: stay.dogs || 'Your stay', breeds: '' };
  const n = nights(stay.checkIn, stay.checkOut);
  return (
    <div className={'cp-card stay compact st-' + stay.status}>
      <div className="hs-who">
        <span className="cs-avs">
          {on.map((d) => (
            <Avatar key={d.id} name={d.name} id={d.id} size={34} kind="dog" initials={initialOf(d.name)} />
          ))}
        </span>
        <div>
          <b>{head.names}</b>
          {head.breeds && <small>{head.breeds}</small>}
        </div>
      </div>
      <div className="hs-when">
        <span>
          {G.sunrise({ size: 13 })}
          <b>{stayDay(stay.checkIn)}</b> {halfOfDay(stay.dropOff)}
        </span>
        <em>
          {n} night{n === 1 ? '' : 's'}
        </em>
        <span>
          {G.sunset({ size: 13 })}
          <b>{stayDay(stay.checkOut)}</b> {halfOfDay(stay.collection)}
        </span>
      </div>
    </div>
  );
}

export function RegistrationReceipt({ me, today }: { me: PortalMe; today: string }) {
  const firstName = me.contact.firstName ?? me.contactName.trim().split(/\s+/)[0] ?? '';
  const { stay, hadOne } = nextStay(me.stays, today);
  const place = townCounty(me.address);

  return (
    <main className="cp-app cp-wizard">
      <div className="cp-scroll cp-step cp-receipt-page">
        <WizardBrand />
        <div className="cp-hero">
          <span className="cp-tick lg">{G.tick({ size: 36, strokeWidth: 2.5 })}</span>
          <h1 className="cp-h1">You’re all set, {firstName}</h1>
          <p className="cp-p cp-lead">{receiptLead(stay !== null)}</p>
        </div>

        <div className="hh-card">
          <div className="hh-grp">
            <span className="hh-lbl">People</span>
            {me.contacts.map((p) => {
              const phone = toIrishNational(p.phone);
              const isMe = p.id === me.contact.id;
              return (
                <div className="hh-row" key={p.id}>
                  <Avatar name={p.name} id={String(p.id)} size={34} initials={initialOf(p.name)} />
                  <div className="hh-t">
                    <b>{p.name}</b>
                    {phone && <small>{phone.national}</small>}
                  </div>
                  {isMe ? <Tag tone="ok">You</Tag> : p.registeredAt ? <Tag tone="neutral">Registered</Tag> : null}
                </div>
              );
            })}
          </div>
          <div className="hh-grp">
            <span className="hh-lbl">Dogs</span>
            {me.dogs.map((d) => {
              const tag = cardTag(d, today);
              return (
                <div className="hh-row" key={d.id}>
                  <Avatar name={d.name} id={d.id} size={34} kind="dog" initials={initialOf(d.name)} />
                  <div className="hh-t">
                    <b>{d.name}</b>
                    <small>{dogLine(d)}</small>
                  </div>
                  <Tag tone={tag.tone} glyph={G.syringe({ size: 12 })}>
                    {tag.text}
                  </Tag>
                </div>
              );
            })}
          </div>
          {place && (
            <div className="hh-foot">
              {G.pin({ size: 13 })}
              <span>{place}</span>
            </div>
          )}
        </div>

        {stay ? (
          <>
            <p className="cp-eyebrow">{hadOne ? 'Your next stay' : 'Your first stay'}</p>
            <CompactStay stay={stay} dogs={me.dogs} />
            <div className="cp-actions">
              <PendingButton className="mp-btn primary" href="/home" icon={G.home({ size: 15 })}>
                Go to stays
              </PendingButton>
            </div>
          </>
        ) : (
          <div className="cp-close plain">
            <span className="cp-next-av">
              {me.dogs[0] && <Avatar name={me.dogs[0].name} id={me.dogs[0].id} size={44} kind="dog" initials={initialOf(me.dogs[0].name)} />}
            </span>
            <b>{closingLine(me.dogs)}</b>
            <small>Pick your dates and Patricia will confirm, usually the same day.</small>
          </div>
        )}
        {/* The way on, pinned to the foot like every page's primary. */}
        {!stay && (
          <div className="cp-actions">
            <PendingButton className="mp-btn primary" href="/home" icon={G.calendar({ size: 15 })}>
              Book a stay
            </PendingButton>
          </div>
        )}
      </div>
    </main>
  );
}
